import { useState } from 'react';

import { Box, Chip, Stack, TextField, Typography } from '@mui/material';

import { themeColors } from 'colors';
import Button from 'domains/core/components/Button';

import { COPY_GENERATOR_FEEDBACK_TAGS, FeedbackTag } from './utils';

export type Props = {
    onSubmit: (tags: FeedbackTag[], reviewText: string) => void;
    onSkip: () => void;
};

const DislikeFeedbackPanel = ({ onSubmit, onSkip }: Props) => {
    const [selectedTags, setSelectedTags] = useState<FeedbackTag[]>([]);
    const [reviewText, setReviewText] = useState('');

    const handleChipToggle = (tag: FeedbackTag) => {
        setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
    };

    return (
        <Box
            aria-label="Dislike feedback panel"
            data-testid="dislike-feedback-panel"
            role="region"
            sx={{
                bgcolor: 'other.paloma',
                borderRadius: '6px',
                mt: 1,
                px: 2,
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
            }}
        >
            {/* Header */}
            <Stack spacing={0.5}>
                <Typography
                    sx={{
                        fontSize: 16,
                        fontWeight: 500,
                        color: 'text.primary',
                        letterSpacing: '0.15px',
                        textTransform: 'lowercase',
                        lineHeight: '24px',
                    }}
                >
                    Thanks for your feedback!
                </Typography>
                <Typography
                    sx={{
                        fontSize: 14,
                        color: 'text.secondary',
                        letterSpacing: '0.4px',
                        lineHeight: '17px',
                    }}
                >
                    Please share more, the response was?
                </Typography>
            </Stack>

            {/* Body */}
            <Stack spacing={1.5}>
                {/* Tag chips */}
                <Stack direction="row" flexWrap="wrap" gap={1}>
                    {COPY_GENERATOR_FEEDBACK_TAGS.map((tag) => {
                        const isSelected = selectedTags.includes(tag);
                        return (
                            <Chip
                                key={tag}
                                aria-label={`${tag}${isSelected ? ', selected' : ''}`}
                                aria-pressed={isSelected}
                                data-testid={`dislike-feedback-tag-${tag}`}
                                label={tag}
                                onClick={() => handleChipToggle(tag)}
                                variant="outlined"
                                sx={{
                                    borderRadius: '6px',
                                    height: 'auto',
                                    fontSize: 13,
                                    letterSpacing: '0.16px',
                                    textTransform: 'lowercase',
                                    py: '3px',
                                    px: '2px',
                                    bgcolor: 'white',
                                    color: 'primary.main',
                                    borderWidth: '1px',
                                    borderColor: isSelected ? 'primary.main' : 'transparent',
                                    '&:hover': { bgcolor: 'action.hover' },
                                    '& .MuiChip-label': {
                                        px: '6px',
                                        py: 0,
                                    },
                                }}
                            />
                        );
                    })}
                </Stack>

                {/* Comments textarea */}
                <TextField
                    fullWidth
                    inputProps={{ 'aria-label': 'Additional comments', 'data-testid': 'dislike-feedback-review-text' }}
                    multiline
                    onChange={(e) => setReviewText(e.target.value)}
                    placeholder="Additional comments"
                    value={reviewText}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '6px',
                            bgcolor: 'white',
                            boxShadow: `1px 1px 0px 0px ${themeColors.primary.main}`,
                            minHeight: 100,
                            alignItems: 'flex-start',
                            py: 2,
                            px: 1.5,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                                borderWidth: '1px',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                                borderWidth: '1px',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                                borderWidth: '1px',
                            },
                        },
                        '& .MuiInputBase-input': {
                            fontSize: 13,
                            letterSpacing: '0.16px',
                            p: 0,
                            '&::placeholder': {
                                color: themeColors.text.disabled,
                                opacity: 1,
                                textTransform: 'lowercase',
                            },
                        },
                    }}
                />

                {/* Actions */}
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    <Button
                        data-testid="dislike-feedback-skip"
                        onClick={onSkip}
                        variant="text"
                        sx={{ fontSize: 14, letterSpacing: '0.5px', py: '5px', px: 2 }}
                    >
                        Skip
                    </Button>
                    <Button
                        data-testid="dislike-feedback-submit"
                        disabled={selectedTags.length === 0 && reviewText.trim() === ''}
                        onClick={() => onSubmit(selectedTags, reviewText)}
                        variant="contained"
                        sx={{ fontSize: 14, letterSpacing: '0.5px', py: '5px', px: 2 }}
                    >
                        Submit
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
};

export default DislikeFeedbackPanel;
